import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Meetup = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild provides a modern alternative to Meetup for running community
            Events. If you're looking to move your community off Meetup, Guild
            offers a comprehensive set of tools to manage your group — with more
            control over your content, branding, and member experience.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-settings-meetup.png'
          alt={t`Meetup migration settings page with options for importing community data from Meetup to Guild`}
          caption={t`The Meetup migration settings help you move your community to Guild`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='why-switch' title={t`Why Switch to Guild?`}>
          <Paragraph size='$5'>
            <Trans>
              Compared to Meetup, Guild includes several features that give
              Organizers more flexibility:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='ticket-diagonal' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    <Link href='/docs/events/ticketing-overview'>Ticketing & Payments</Link>
                  </Paragraph>{' '}
                  — Create paid Events with multiple ticket tiers, powered by Stripe.
                  Set different prices for early bird, general admission, or VIP access.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='projection-screen' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    <Link href='/docs/presentations/overview'>Presentations</Link>
                  </Paragraph>{' '}
                  — Share recorded talks, slide decks, and session content alongside
                  your Events. Build a permanent library of your community's knowledge
                  that Members can browse anytime.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    <Link href='/docs/guilds/members'>Member roles</Link>
                  </Paragraph>{' '}
                  — Fine-grained roles and permissions with Organizer, Editor, and
                  Member tiers. Delegate content management to trusted co-organizers
                  without giving them full admin access.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='window-new' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    <Link href='/docs/guilds/embeds'>Embeds</Link>
                  </Paragraph>{' '}
                  — Display your Guild's Events on your own website with an embeddable
                  widget.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Integrations</Paragraph> — Connect
                  to <Link href='/docs/guilds/bluesky'>Bluesky</Link> for automatic Event
                  announcements and{' '}
                  <Link href='/docs/guilds/google-calendar'>Google Calendar</Link> for
                  personal Event syncing.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
          <Paragraph size='$5'>
            <Trans>
              Each <Link href='/docs/guilds/overview'>Guild</Link> gets its own
              customizable profile page with a shareable URL — your community's
              permanent home on the web.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='migration-process' title={t`How to Migrate`}>
          <Paragraph size='$5'>
            <Trans>
              Moving your community from Meetup to Guild is straightforward. Here's
              the recommended approach:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people-community' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>1. Create your Guild</Paragraph>{' '}
                  — Follow the steps in{' '}
                  <Link href='/docs/guilds/creation'>Creating a Guild</Link> to set up
                  your new community home. Choose a name and short name that matches
                  your Meetup group so existing members can find you easily.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='image' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    2. Set up your profile
                  </Paragraph>{' '}
                  — Add your cover photo, profile photo, and description. You can reuse
                  the same branding from your Meetup group to make the transition feel
                  familiar to your members.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='calendar-add' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    3. Create your upcoming Events
                  </Paragraph>{' '}
                  — Add any upcoming Events to your Guild. See{' '}
                  <Link href='/docs/events/creation'>Creating an Event</Link> for a
                  step-by-step guide.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='person-add' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    4. Invite your community
                  </Paragraph>{' '}
                  — Share your Guild's URL with your existing Meetup members. You can
                  announce the move on Meetup and include the link in your next Event
                  description or group message.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    5. Set up integrations
                  </Paragraph>{' '}
                  — Connect <Link href='/docs/guilds/bluesky'>Bluesky</Link> for social
                  announcements,{' '}
                  <Link href='/docs/guilds/calendar-feeds'>Calendar Feeds</Link> for
                  calendar subscriptions, and{' '}
                  <Link href='/docs/guilds/monetization'>Stripe</Link> if you plan to
                  sell tickets.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='what-transfers' title={t`What Transfers`}>
          <Paragraph size='$5'>
            <Trans>
              Guild does not offer an automated import from Meetup, but migration is
              simple because the most important parts of a community are its people
              and its upcoming Events — not historical data. Your existing members
              join your Guild individually, and you recreate your upcoming Events on
              the platform.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Past Event history from Meetup does not transfer. However, once you
              start hosting Events on Guild, all attendance records, Presentations,
              and community history are preserved going forward.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Meetup
